import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from 'src/lib/utils';
import env from 'env';
import {
  BillingTerminatedVsTotalCountsDocument,
  type IBillingTerminatedVsTotalCountsQuery,
} from 'src/graphql/actions/billing/billingTerminatedVsTotalCounts.query';
import { apolloCache as cache } from 'src/graphql';
import { BillingDocument, type IBillingQuery } from 'src/graphql/actions/billing/billing.query.generated';

import { solutionsMap } from '../model';

import CallToActionBanner from './CallToActionBanner';
import GetOfferButton from './GetOfferButton';

type ISoftwareCard = {
  type: keyof typeof solutionsMap;
  count?: number;
};

const SoftwareCard = ({ type, count = 0 }: ISoftwareCard) => {
  const { title, description, emptyDescription, buttonLink, buttonText, activeLabel, activeColor } = solutionsMap[type];

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-6 rounded-lg border border-border shadow-sm">
      <div className="grow-1 space-y-2.5">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium">{title}</div>
          {Boolean(count) && <div className="size-1.5 rounded-full bg-orange-700" />}
        </div>
        {Boolean(count) && (
          <div className="text-xs text-muted-foreground">
            <strong className={cn('text-2xl font-bold', activeColor)}>{count}</strong> {activeLabel}
          </div>
        )}
        <p className="text-xs text-muted-foreground">{count ? description : emptyDescription}</p>
      </div>
      <div className="shrink-0 [&>a]:w-full sm:[&>a]:w-auto">
        <Button variant="secondary" asChild>
          <Link to={buttonLink} className="text-foreground!">
            {buttonText} <ArrowRight />
          </Link>
        </Button>
      </div>
    </div>
  );
};

const ReviewAccountUsage = () => {
  const dataSolutions = cache.readQuery<IBillingTerminatedVsTotalCountsQuery>({
    query: BillingTerminatedVsTotalCountsDocument,
  });
  const dataBilling = cache.readQuery<IBillingQuery>({
    query: BillingDocument,
  });

  return (
    <div className="overflow-y-auto space-y-6 -mx-6 px-6">
      <div className="space-y-4">
        <SoftwareCard
          type="backup"
          count={(dataSolutions?.allBackups?.nodesCount ?? 0) - (dataSolutions?.removedBackups?.nodesCount ?? 0)}
        />
        <SoftwareCard
          type="softwareEscrow"
          count={
            (dataSolutions?.allSoftwareEscrows?.nodesCount ?? 0) -
            (dataSolutions?.terminatedSoftwareEscrows?.nodesCount ?? 0)
          }
        />
        <SoftwareCard
          type="saasEscrow"
          count={
            (dataSolutions?.allSaasEscrows?.nodesCount ?? 0) - (dataSolutions?.terminatedSaasEscrows?.nodesCount ?? 0)
          }
        />
        <SoftwareCard
          type="continuity"
          count={
            (dataSolutions?.allContinuityEscrows?.nodesCount ?? 0) -
            (dataSolutions?.terminatedContinuityEscrows?.nodesCount ?? 0)
          }
        />
        <SoftwareCard
          type="custodian"
          count={
            (dataSolutions?.allCustodianProfiles?.nodesCount ?? 0) -
            (dataSolutions?.terminatedCustodianProfiles?.nodesCount ?? 0)
          }
        />
      </div>
      {Boolean(dataBilling?.billing?.policy.requestExclusiveOffer) && (
        <CallToActionBanner
          title="Exclusive offer: keep your plan for less"
          description="To thank you for your business, we’re offering an exclusive loyalty discount. Continue enjoying all features and protections — now at a reduced rate."
          action={<GetOfferButton />}
        />
      )}
      <p className="text-sm text-muted-foreground">
        Need some help? If you’re having trouble with Codekeeper application,{' '}
        <a href={`mailto:${env.CODEKEEPER_SERVICE_EMAIL}`} className="text-foreground! underline! hover:no-underline!">
          please get in touch
        </a>
        .
      </p>
    </div>
  );
};

export default ReviewAccountUsage;
